/**
 * FeatureStateMessages.tsx
 *
 * This file contains reusable, memoized React components for displaying various feature state messages
 * in the Explore Features UI. These include:
 * - LoadingState: Shows a spinner and loading text while feature data is being fetched.
 * - ErrorState: Displays an error message if feature data fails to load.
 * - EmptyState: Warns when no feature data is available for the selected product.
 * - NoFeatureSelectedState: Informs the user to select at least one feature to display the chart.
 *
 * All components are wrapped with memo for performance optimization, preventing unnecessary re-renders.
 * Styles are imported from ExploreFeatures.styles.ts for consistency and maintainability.
 */

import { MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import { memo } from "react";
import { loadingStateStackStyle } from "../ExploreFeatures.styles";
import { FeatureStateMessagesProps } from "../ExploreFeatures.interfaces";

const LoadingStateComponent = () => (
  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} styles={loadingStateStackStyle}>
    <Spinner size={SpinnerSize.small} />
    <Text>Loading feature data...</Text>
  </Stack>
);
LoadingStateComponent.displayName = "LoadingState";
export const LoadingState = memo(LoadingStateComponent);

const ErrorStateComponent = () => (
  <MessageBar messageBarType={MessageBarType.error}>Error loading feature data. Please try again.</MessageBar>
);
ErrorStateComponent.displayName = "ErrorState";
export const ErrorState = memo(ErrorStateComponent);

const EmptyStateComponent = ({ productName }: FeatureStateMessagesProps) => (
  <MessageBar messageBarType={MessageBarType.warning}>No feature data available for {productName}.</MessageBar>
);
EmptyStateComponent.displayName = "EmptyState";
export const EmptyState = memo(EmptyStateComponent);

const NoFeatureSelectedStateComponent = () => (
  <MessageBar messageBarType={MessageBarType.info}>Please select at least one feature to display the chart.</MessageBar>
);
NoFeatureSelectedStateComponent.displayName = "NoFeatureSelectedState";
export const NoFeatureSelectedState = memo(NoFeatureSelectedStateComponent);
